import {
 useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import i18n from '../../../i18n';

import Api from '@common/services/api';
import type { FailedRequest } from '@common/services/api';
import { AlertService } from '@common/services/alert';
import { predicatesToPayload } from '@common/utils/predicates';
import type { Predicate } from '@common/types/objects';
import { ESearchPredicateTypes } from '@common/definitions';
import type { ListApiResponse } from '@common/types/util-types';

import {
  type AudienceSearchModule,
  emptyAudienceUserSearchResponse,
  shouldSkipEmptyAudienceUserSearch,
  usersSearchColleagueScopingQuery,
} from './audience-user-search';

export type AudienceUserSearchFilter = {
  predicate_type: ESearchPredicateTypes;
  predicates: Predicate[];
  search?: string | null;
};

export type AudienceUserSearchResponse = ListApiResponse<
  Record<string, unknown>[],
  undefined,
  { pagination: { total_count: number; offset?: number; limit?: number } }
>;

interface UseAudienceUserSearchOptions {
  organisationId: string;
  filter: AudienceUserSearchFilter;
  limit?: number;
  enabled?: boolean;
  allowOrganisationWideAudienceSearch?: boolean;
  audienceSearchModule?: AudienceSearchModule;
  onSuccess?: (result: AudienceUserSearchResponse) => void;
}

export function useAudienceUserSearch({
  organisationId,
  filter,
  limit = 10,
  enabled = true,
  allowOrganisationWideAudienceSearch = true,
  audienceSearchModule,
  onSuccess,
}: UseAudienceUserSearchOptions) {
  const filterSignature = JSON.stringify(filter);
  const stableFilter = useMemo(
    () => filter,
    [filterSignature]
  );

  const [data, setData] = useState<AudienceUserSearchResponse['data']>([]);
  const [meta, setMeta] = useState<AudienceUserSearchResponse['meta']>({
    pagination: {
      total_count: 0,
      offset: 0,
      limit,
    },
  } as AudienceUserSearchResponse['meta']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const offsetRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const latestRequestIdRef = useRef(0);
  const inFlightRef = useRef(false);
  const onSuccessRef = useRef(onSuccess);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  const fetchUsers = useCallback(async (reset = false) => {
    if (!enabled || !organisationId) return;

    if (!reset && inFlightRef.current) return;

    if (
      shouldSkipEmptyAudienceUserSearch(
        allowOrganisationWideAudienceSearch,
        stableFilter.predicates
      )
    ) {
      const empty = emptyAudienceUserSearchResponse(limit) as AudienceUserSearchResponse;
      setData(empty.data);
      setMeta(empty.meta);
      onSuccessRef.current?.(empty);
      return;
    }

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    const isSuperseded = () => requestId !== latestRequestIdRef.current;

    const requestOffset = reset ? 0 : offsetRef.current;

    inFlightRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const query = Api.utils.toQuery({
        offset: requestOffset,
        limit,
        include: ['networks', 'functions'],
        q: stableFilter.search,
        ...usersSearchColleagueScopingQuery(audienceSearchModule),
      });

      const result = await Api.post<AudienceUserSearchResponse>(
        `/v1/organisations/${organisationId}/users/search?${query}`,
        {
          predicate_type: stableFilter.predicate_type,
          predicates: predicatesToPayload(stableFilter.predicates),
        },
        true,
        { signal: abortControllerRef.current.signal },
      );

      if (isSuperseded()) return;

      setData((prev) => {
        const merged = reset ? result.data : [...prev, ...result.data];
        offsetRef.current = merged.length;
        return merged;
      });
      setMeta(result.meta);
      setLoading(false);
      inFlightRef.current = false;
      onSuccessRef.current?.(result);
    } catch (err: unknown) {
      if ((err as { name?: string })?.name === 'AbortError' || isSuperseded()) {
        inFlightRef.current = false;
        return;
      }

      const failed = err as FailedRequest;
      if (failed?.status_code === 403) {
        AlertService.warning(i18n.t('common:predicates_filter_audience_search_forbidden'));
        const empty = emptyAudienceUserSearchResponse(limit) as AudienceUserSearchResponse;
        setData(empty.data);
        setMeta(empty.meta);
        onSuccessRef.current?.(empty);
      }

      const nextError = err instanceof Error ? err : new Error(String(err));
      setError(nextError);
      setLoading(false);
      inFlightRef.current = false;
    }
  }, [
    enabled,
    organisationId,
    limit,
    stableFilter,
    allowOrganisationWideAudienceSearch,
    audienceSearchModule,
  ]);

  const reset = useCallback(() => {
    offsetRef.current = 0;
    setData([]);
    return fetchUsers(true);
  }, [fetchUsers]);

  const refetch = useCallback(() => fetchUsers(false), [fetchUsers]);

  useEffect(() => {
    if (!enabled) return undefined;

    offsetRef.current = 0;
    inFlightRef.current = false;
    setData([]);
    fetchUsers(true);

    return () => {
      abortControllerRef.current?.abort();
      inFlightRef.current = false;
    };
    // Refetch when filter content changes — not when fetchUsers identity changes.
    // eslint-disable-next-line -- filterSignature
  }, [
    enabled,
    filterSignature,
    organisationId,
    limit,
    allowOrganisationWideAudienceSearch,
    audienceSearchModule,
  ]);

  useEffect(() => () => {
    abortControllerRef.current?.abort();
  }, []);

  return {
    data,
    setData,
    meta,
    loading,
    error,
    refetch,
    reset,
  };
}
